import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { AuthProvider } from './hooks/useAuth'
import './styles/globals.css'
import { initializeSentry, Sentry } from './lib/sentry'
import { SentryFallback } from './components/SentryFallback'
import { installImpersonationFetch } from './lib/impersonationFetch'
import { installAuthRetryFetch } from './lib/authRetryFetch'

// Guard against DOM-mutating browser features/extensions (Google Translate,
// Grammarly, password managers) that swap React-managed nodes out from under
// the reconciler. Without this, React's commit-phase removeChild/insertBefore
// throws "NotFoundError: node is not a child of this node" (React #11538).
// No-op the call when the node isn't actually where React thinks it is.
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) return child
    return originalRemoveChild.call(this, child) as T
  }
  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) return newNode
    return originalInsertBefore.call(this, newNode, referenceNode) as T
  }
}

initializeSentry()

// Attach the impersonating admin's identity to worker calls when an admin is
// impersonating a brand (see impersonationFetch). Must run before any fetch.
installImpersonationFetch()
// Auto-refresh + retry worker calls that 401 on a stale/expired token. Installed
// AFTER impersonation so its retry still flows through the impersonation wrapper.
installAuthRetryFetch()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack, resetError }) => (
        <SentryFallback error={error} componentStack={componentStack} resetError={resetError} />
      )}
    >
      <BrowserRouter>
        {/* One auth subscription for the whole app — see hooks/useAuth.tsx. */}
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </StrictMode>
)
